import api from 'src/assets/api/gql'
import SessionChatMessages from 'src/graphql/session/SessionChatMessages.gql'
import SessionChatMessageSend from 'src/graphql/session/SessionChatMessageSend.gql'
import SessionChatMessageSender from 'src/graphql/session/SessionChatMessageSender.gql'
import AttendeeSessionChatMessages from 'src/graphql/attendee/AttendeeSessionChatMessages.gql'
import AttendeeSessionChatMessageSend from 'src/graphql/attendee/AttendeeSessionChatMessageSend.gql'
import AttendeeSessionChatMessageSender from 'src/graphql/attendee/AttendeeSessionChatMessageSender.gql'
import { PersonPublicProfile } from 'src/graphql/types'

export default class Chat {
  sessionId = 0
  attendeeId = ''
  history: Json[] = []
  senders = new Map<number, PersonPublicProfile>()

  setSender (sender: PersonPublicProfile) {
    this.senders.set(sender.personId || 0, sender)
  }

  setSessionId (sessionId: number) {
    this.sessionId = sessionId
  }

  setAttendeeId (attendeeId: string) {
    this.attendeeId = attendeeId
  }

  async load (done: (stop?: boolean) => void) {
    const before = this.history.length ? this.history[0].messages[0].id : 0
    const count = 20
    let history: Json[] = []
    if (this.attendeeId) {
      history = await this.loadAttendeeHistory(before, count)
    } else {
      history = await this.loadPresenterHistory(before, count)
    }
    for (const message of history.reverse()) {
      this.messagePrepend(this.history, message)
    }
    done(this.history.length === 0 || this.history[0].messages[0].id === 1)
  }

  async loadAttendeeHistory (before: number, count: number) {
    const { data: { attendeeResponse } } = await api.query({
      query: AttendeeSessionChatMessages,
      variables: {
        id: this.attendeeId,
        before, count
      }
    })
    return attendeeResponse?.responseSession?.chatMessages || [] as Json[]
  }

  async loadPresenterHistory (before: number, count: number) {
    const { data: { session } } = await api.query({
      query: SessionChatMessages,
      variables: {
        id: this.sessionId,
        before, count
      }
    })
    return session?.chatMessages || [] as Json[]
  }

  messagePrepend (history: Json[], message: Json) {
    const firstMessage = history.length ? history[ 0 ] : null
    if (firstMessage && firstMessage.sender.personId === message.by) {
      if (firstMessage.messages[0].id !== message.id) {
        firstMessage.messages.unshift(message)
      }
    } else {
      history.unshift(this.createHistoryFirstMessage(message))
    }
  }

  messageAppend (history: Json[], message: Json) {
    const lastMessage = history.length ? history[ history.length - 1 ] : null
    if (lastMessage && lastMessage.sender.personId === message.by) {
      if (lastMessage.messages[ lastMessage.messages.length - 1 ].id !== message.id) {
        lastMessage.messages.push(message)
      }
    } else {
      history.push(this.createHistoryFirstMessage(message))
    }
  }

  createHistoryFirstMessage (message: Json) {
    return {
      sender: this.getSender(message),
      messages: [message]
    }
  }

  getSender (message: Json) {
    return this.senders.get(message.by) || this.loadSender(message)
  }

  loadSender (message: Json) {
    if (message.guest === undefined) {
      const sender = {
        personId: message.by,
        name: '',
        avatarUrl: ''
      }
      this.senders.set(sender.personId, sender as PersonPublicProfile)
      if (this.attendeeId) {
        this.loadAttendeeSenderData(sender, message.id)
      } else {
        this.loadPresenterSenderData(sender, message.id)
      }
      return sender
    } else {
      const sender = {
        personId: message.by,
        guest: message.guest
      }
      this.senders.set(sender.personId, sender as PersonPublicProfile)
      return sender
    }
  }

  loadAttendeeSenderData (sender: Json, messageId: number) {
    api.query({
      query: AttendeeSessionChatMessageSender,
      variables: {
        id: this.attendeeId,
        messageId
      }
    }).then(({ data: { attendeeResponse } }) => {
      Object.assign(sender, attendeeResponse?.responseSession?.chatSenderPublicProfile || {})
    })
  }

  loadPresenterSenderData (sender: Json, messageId: number) {
    api.query({
      query: SessionChatMessageSender,
      variables: {
        id: this.sessionId,
        messageId
      }
    }).then(({ data: { session } }) => {
      Object.assign(sender, session?.chatSenderPublicProfile || {})
    })
  }

  async send (message: string) {
    if (this.attendeeId) {
      await this.sendAttendee(message)
    } else {
      await this.sendPresenter(message)
    }
  }

  async sendAttendee (message: string) {
    await api.mutate({
      query: AttendeeSessionChatMessageSend,
      variables: {
        input: {
          attendeeId: this.attendeeId,
          message
        }
      }
    })
  }

  async sendPresenter (message: string) {
    await api.mutate({
      query: SessionChatMessageSend,
      variables: {
        input: {
          sessionId: this.sessionId,
          message
        }
      }
    })
  }
}
